import {Formik, useFormik} from "formik";
import {Dispatch, SetStateAction, useContext, useEffect, useState} from "react";
import { Button, Col, Form, InputGroup, Modal, Row } from "react-bootstrap";
import { IConfigTable } from "../../models/OctResponse";
import {CODE_SUCCESS, CriteriaLevels, ERROR_MESSAGE, MESSAGE} from "../../utils/Constant";
import CriteriaTable from "../components/CriteriaTable";
import {
  EVALUATE_STATUS,
  RESULT,
  TYPE_MODAL_FORM,
} from "../constants/reportDetailModal";
import ReportEvaluationModal from "./ReportEvaluationModal";
import {
  getLogAssessment,
  getSubSessionByPlanCriteria,
} from "../services/ReportEvaluation";
import { toast } from "react-toastify";
import { AppContext } from "../../appContext/AppContext";
import {generateForm, getCriteriaLevels} from "../../utils/AppFunction";
import GenerateFormComponent from "../../component/generate-form/GenerateFormHTMLNoValid";
import ModalHeader from "../../component/ModalHeader";
import {useReportEvaluationContext} from "../context/ReportEvaluationContext";

type Props = {
  show: boolean;
  handleCloseDialog: () => void;
};

const ReportDetailModal = (props: Props) => {
  const { setIsLoading } = useContext(AppContext);
  const {setItemDetail, itemDetail, isView} = useReportEvaluationContext();
  const {show, handleCloseDialog} = props;
  const [objectSearch, setObjectSearch] = useState<any>();
  const [configTable, setConfigTable] = useState<IConfigTable>({
    totalPages: 1,
    totalElements: 5,
    numberOfElements: 5,
  });
  const [dataTable, setDataTable] = useState<any>();
  const [dataTableDetailRow, setDataTableDetailRow] = useState<any>();
  const [showEvaluateDetailsModal, setShowEvaluateDetailsModal] = useState(false);
  const [tableCache, setTableCache] = useState<{ [key: string]: any[] }>({});
  const [reload, setReload] = useState(false);
  const [saved, setSaved] = useState(false);
  const [formJson, setFormJson] = useState<any>([]);

  useEffect(() => {
    updateForm().then(r => r);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateForm = async () => {
    const data = await generateForm(TYPE_MODAL_FORM, setIsLoading);
    setFormJson(data || []);
  };

  const handleSubmit = (values: any) => {
    const newValues = {
      planId: itemDetail?.planId,
      criteriaId: itemDetail?.criteriaId,
      planCriteriaId: itemDetail?.id,
      level: values?.level?.id,
    };
    setObjectSearch(newValues);
  }

  useEffect(() => {
    if (itemDetail) {
      const newCriteria = {
        ...itemDetail,
        chapterText: {
          name: `${itemDetail?.chapterCode}: ${itemDetail?.chapterName}`
        },
      };
      setItemDetail(newCriteria)
      setObjectSearch({
        planId: itemDetail?.planId,
        criteriaId: itemDetail?.criteriaId,
        planCriteriaId: itemDetail?.id,
        level: CriteriaLevels.LEVEL_1,
      });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (itemDetail) {
      setObjectSearch({
        planId: itemDetail?.planId,
        criteriaId: itemDetail?.criteriaId,
        planCriteriaId: itemDetail?.id,
        level: CriteriaLevels.LEVEL_1,
      });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGetSubSessionByPlanCriteria = async (
    forceReload: boolean = false
  ): Promise<void> => {
    const key = objectSearch?.level;
    const cachedData = tableCache[key];

    if (!forceReload && cachedData) {
      setDataTable(cachedData);
      return;
    }

    const { planCriteriaId, ...rest } = objectSearch;
    setIsLoading(true);

    try {
      const { data } = await getSubSessionByPlanCriteria(rest);
      if (data?.code === CODE_SUCCESS) {
        const newData = data?.data?.map((item: any) => ({
          ...item,
          pointSelfAssessment:
            RESULT.find(
              (result: any) => result.id === item?.pointSelfAssessment
            ) || null,
          conclusionAssessment:
            RESULT.find(
              (result: any) => result.id === item?.conclusionAssessment
            ) || null,
          evidence: {
            id: item?.fileId,
            name: item?.fileName,
          },
        }));

        const checkStatusEvaluated = newData?.every(
          (item: any) => +item?.status === EVALUATE_STATUS.EVALUATED.code
        );

        if (checkStatusEvaluated) {
          setSaved(true);
        } else {
          setSaved(false);
        }

        setDataTable(newData);

        setConfigTable({
          totalPages: data.data.totalPages,
          totalElements: data.data.totalElements,
          numberOfElements: data.data.numberOfElements,
        });

        setTableCache((prev) => ({
          ...prev,
          [key]: newData,
        }));
      }
    } catch (error) {
      console.error("error", error);
      toast.error(ERROR_MESSAGE);
    } finally {
      setIsLoading(false);
    }
  };

  // Khi objectSearch thay đổi (ví dụ: chuyển sang level khác), dùng cache nếu có
  useEffect(() => {
    if (objectSearch?.level) {
      handleGetSubSessionByPlanCriteria(false);
    }
  }, [objectSearch]);

  // Khi reload (ấn Save), luôn gọi lại API và ghi đè cache
  useEffect(() => {
    if (objectSearch?.level) {
      handleGetSubSessionByPlanCriteria(true);
    }
  }, [reload]);

  const handleDoubleClick = async (type: string, data: any) => {
    if (!data?.id) {
      return toast.warning("Vui lòng đánh giá trước khi xem chi tiết");
    }

    const params = {
      assessmentId: data?.id,
    };
    setIsLoading(true);
    try {
      const { data } = await getLogAssessment(params);
      if (data?.code === CODE_SUCCESS) {
        setDataTableDetailRow(data?.data);
        setShowEvaluateDetailsModal(true);
      }
    } catch (error) {
      console.error("error", error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderSearchBtn = (props: { data: any }) => {
    const label = props.data?.dataComponent?.label || "";

    return (
      <div className="align-items-center d-flex h-100">
        <Button className="btn btn-fill min-w-120px spaces mx-3">
          {label || ""}
        </Button>
      </div>
    )
  }

  return (
    <Modal
      className="page-full"
      fullscreen
      dialogClassName="h-modal"
      size="xl"
      show={show}
      animation={false}
      centered
      backdropClassName="spaces top-50"
    >

      <Formik
        enableReinitialize
        initialValues={{[TYPE_MODAL_FORM]: {...itemDetail}}}
        onSubmit={handleSubmit}
      >
        {({ values, touched, errors, setValues, handleChange, handleSubmit, setFieldValue }) => (
          <Form onSubmit={handleSubmit}>

            <ModalHeader onHide={handleCloseDialog} title="Chi tiết báo cáo" />
            <Modal.Body className="px-0 py-0 spaces overflow-hidden h-calc-vh-90 bg-white">
              <div className="spaces px-10">
                <GenerateFormComponent
                  isView={isView}
                  listFieldAuto={formJson}
                  handleChange={handleChange}
                  setValues={setValues}
                  values={values}
                  touched={touched}
                  errors={errors}
                  setFieldValue={setFieldValue}
                  modelID={TYPE_MODAL_FORM}
                  DynamicClass={"pl-38"}
                  customComponent={renderSearchBtn}
                />
              </div>
              <div className="spaces px-10 pt-20">
                <CriteriaTable
                  dataTable={dataTable}
                  setDataTable={setDataTable}
                  objectSearch={objectSearch}
                  setObjectSearch={setObjectSearch}
                  configTable={configTable}
                  setConfigTable={setConfigTable}
                  handleDoubleClick={handleDoubleClick}
                  tableCache={tableCache}
                  setTableCache={setTableCache}
                  reload={reload}
                  setReload={setReload}
                />
              </div>

              {showEvaluateDetailsModal && (
                <ReportEvaluationModal
                  show={showEvaluateDetailsModal}
                  handleCloseDialog={() => setShowEvaluateDetailsModal(false)}
                  dataTableDetailRow={dataTableDetailRow}
                />
              )}
            </Modal.Body>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default ReportDetailModal;
